import { Button } from "@/components/ui/button";
import { ArrowUpRight, Download, Plus } from "lucide-react";

import { useWidgetAnalytics } from "@/context/AnalyticsProvider";
import { useCheckout } from "@/context/CheckoutProvider";
import { useRightNav } from "@/context/RightNavContext";
import type { OpenWidgetTabFrom, WidgetTab } from "@/context/RightNavContext";
import { useEffect, useState } from "react";

interface WidgetsMenuProps {
  isDrawerMenu: boolean;
  section: OpenWidgetTabFrom;
}

export function WidgetsMenu({ isDrawerMenu, section }: WidgetsMenuProps) {
  const { openWidgetTab } = useRightNav();
  const { trackWidgetOpen } = useWidgetAnalytics();
  const { checkoutClient } = useCheckout();
  const [fundTab, setFundTab] = useState<WidgetTab>("Swap");
  const [isSwapAvailable, setIsSwapAvailable] = useState<boolean | undefined>(
    undefined,
  );

  // Check if swap is available in user's region
  useEffect(() => {
    checkoutClient.isSwapAvailable().then(setIsSwapAvailable);
  }, [checkoutClient]);

  // Update fundTab based on swap availability
  useEffect(() => {
    // Wait until swap availability check is complete
    if (typeof isSwapAvailable === "undefined") {
      return;
    }

    // Default to Bridge if swap is not available, otherwise Swap
    setFundTab(isSwapAvailable ? "Swap" : "Bridge");
  }, [isSwapAvailable]);

  const borderStyle = isDrawerMenu
    ? "border-2 border-transparent hover:border-white"
    : "border-2";
  const buttonStyle = `flex-1 h-8 rounded-[22px] gap-2 text-white hover:text-white hover:bg-white/10 ${borderStyle}`;

  const handleFundClick = () => {
    trackWidgetOpen({
      widgetTab: fundTab,
      id: "fund-button",
      type: "button",
      label: "Fund",
      section,
    });
    openWidgetTab({ tab: fundTab, from: section });
  };

  const handleSendClick = () => {
    trackWidgetOpen({
      widgetTab: "Send",
      id: "send-button",
      type: "button",
      label: "Send",
      section,
    });
    openWidgetTab({ tab: "Send", from: section });
  };

  const handleReceiveClick = () => {
    trackWidgetOpen({
      widgetTab: "Receive",
      id: "receive-button",
      type: "button",
      label: "Receive",
      section,
    });
    openWidgetTab({ tab: "Receive", from: section });
  };

  return (
    <div className="my-3">
      <div className="flex gap-2 px-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleFundClick}
          className={buttonStyle}
        >
          <Plus className="w-4 h-4" />
          <span className="text-xs font-medium leading-4">Fund</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSendClick}
          className={buttonStyle}
        >
          <ArrowUpRight className="w-4 h-4" />
          <span className="text-xs font-medium leading-4">Send</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReceiveClick}
          className={buttonStyle}
        >
          <Download className="w-4 h-4" />
          <span className="text-xs font-medium leading-4">Receive</span>
        </Button>
      </div>
    </div>
  );
}
